import { css, Global } from '@emotion/react';
import {
  BoardExpState,
  useBoardExpStore,
} from '@sketch-note/design-system/src/components/board-exp';
import { LoaderXs } from '@sketchnote/shared-components';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { API } from 'src/api';
import { NoAccessPage } from 'src/components/error-pages/NoAccessPage';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayout } from 'src/shared/hooks/use-layout';

import { BoardHeader } from '../components/BoardHeaders';
import { BoardView } from '../components/BoardView';
import { useBoardStore } from '../store/useBoardStore';

export const BoardPage = ({ boardId, type }: { boardId: string; type: string }) => {
  const boardsStore = useBoardStore();
  const router = useRouter();
  const templateId = router.query.templateId as string;
  const { setSlackChannel, setIntegrationId, setOnRemoveSlackChannel } = useBoardExpStore();
  const { splitView, splitViewFirstFile, splitViewSecondFile } = useLayout();
  const authData = useAuth((s) => s.userDetails);
  const fetchUserDetails = useAuth((s) => s.fetchUserDetails);
  async function fetchBoard() {
    useBoardStore.setState({ isLoading: true });
    await boardsStore.actions.fetchBoard(boardId);
    useBoardStore.setState({ isLoading: false });
  }

  async function fetchPublicBoard() {
    useBoardStore.setState({ isLoading: true });
    await boardsStore.actions.fetchPublicBoardDetails({
      id: router.query.templateId as string,
      boardId: router.query.view as string,
    });
    useBoardStore.setState({ isLoading: false });
  }

  useEffect(() => {
    if (boardId) {
      if (templateId) fetchPublicBoard();
      else fetchBoard();

      boardsStore.actions.fetchLabel({ id: boardId });
      boardsStore.actions.fetchSelectables({ id: boardId });
    }
  }, [router?.query?.view]);

  useEffect(() => {
    if (boardId) {
      fetchBoard();
    }
  }, [router?.query?.type, splitView, splitViewFirstFile, splitViewSecondFile]);

  const onHandleSetQuickFilter = async () => {
    const data = await fetchUserDetails();
    //Updating with response to avoid stale value
    if (data) {
      boardsStore.actions.setShowQuickFilter(data?.showQuickFilter);
    }
  };

  useEffect(() => {
    onHandleSetQuickFilter();
  }, [authData?.showQuickFilter]);

  useEffect(() => {
    router?.query?.slug && fetchBoard();

    router?.query?.slug && boardsStore.actions.fetchTimer();
    router?.query?.slug && boardsStore.actions.fetchLabel({ id: boardId });
    router?.query?.slug &&
      boardsStore.actions.fetchSelectables({ id: router?.query?.view as string });
    return () => useBoardStore.setState({ workspace: '' });
  }, [router?.query?.slug]);

  const handleDeleteModule: BoardExpState['onRemoveSlackChannel'] = useCallback(async ({ id }) => {
    const response = await API.delete(`/integrations?type=slack&id=${id}`);
    if (response.data.success) {
      setSlackChannel(null);
      setIntegrationId('');
      return { status: 'success' };
    }
    return { status: 'failed' };
  }, []);

  useEffect(() => {
    if (boardsStore.apps?.length) {
      const slackApp = boardsStore.apps.find((app) => app.type === 'slack');
      if (slackApp?.channel) {
        setSlackChannel(slackApp.channel);
        setIntegrationId(slackApp._id);
        setOnRemoveSlackChannel(handleDeleteModule);
      }
    } else {
      setSlackChannel('');
      setIntegrationId('');
    }
  }, [boardsStore]);
  return (
    <div className="relative">
      {boardsStore.authorisation ? (
        <div
          id="board-wrapper"
          className={`${
            ['calendar', 'list'].includes(router.query.type as string) ? 'bg-white' : 'bg-gray-100'
          }`}
        >
          <div className="w-full ">
            <div
              className="overflow-auto mx-auto h-full no-scrollbar"
              style={{ width: 'calc(100%)' }}
            >
              {(!boardsStore.id || boardsStore.id !== router.query.slug) &&
              boardsStore.isLoading ? (
                <div className="h-screen w-full mt-48">
                  <div className="overflow-hidden">
                    <LoaderXs className="mx-auto text-xss" />
                  </div>
                </div>
              ) : (
                <div>
                  {router.query?.type !== 'list' && !templateId && (
                    <BoardHeader boardId={boardId} type={type} />
                  )}
                  <BoardView type={type} id={boardId} />
                </div>
              )}
            </div>
          </div>
          <Global
            styles={css`
              body {
                overflow: hidden;
              }
              html,
              body {
                overscroll-behavior: none;
              }
            `}
          />
        </div>
      ) : (
        <NoAccessPage showLogo={false} type={'board'} />
      )}
    </div>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return { props: {} };
};
